import Styles from './PickBackground.module.scss'
import * as CSS from 'csstype'
import { Button } from 'blaze-ui/Button'
import { MobileWizard } from 'components/Wizards/MobileWizards/Layout'
import { Selection } from '@react-types/shared'
import { useMobileTemplatePreviewDimensions } from 'components/Wizards/MobileWizards/hooks/useMobileTemplatePreviewDimensions'
import { DesignChannel, getAspectRatioKeyFromDesignChannel } from 'components/NewFileModal/Types'
import { multiStep } from 'components/Wizards/MobileWizards/MultiStepMobileWizard/multiStep'
import { useField } from 'formik'
import { useWizardSchema } from 'components/Wizards/Wizard/WizardContext'
import { MultiStepSchema } from 'components/Wizards/schemas/Types'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { TemplateDrawer } from 'components/Templates/TemplateDrawer'
import { TemplatesDrawerV2 } from 'components/Templates/TemplatesModalV2/TemplatesDrawerV2'
import { noop } from 'lodash'
import { TemplatesGrid } from '../ChooseTemplate/TemplatesGrid'
import { useVisualTemplates } from 'hooks/useVisualTemplates'
import { collectResults } from 'utils/query'
import { StepProps } from 'components/Wizards/MobileWizards/MultiStepMobileWizard/Types'

type Props = StepProps & {
  templateBorderRadius?: number | string
}

export const PickBackground = observer(
  multiStep(({ schema, submit, templateBorderRadius }: Props) => {
    const { meta } = schema.describe()
    const { drawersStore } = useRootStore()
    const wizardSchema = useWizardSchema() as MultiStepSchema
    const channel = wizardSchema.meta.channel as DesignChannel
    const [{ value: width }] = useField('width')
    const [{ value: height }] = useField('height')
    const [{ value: removeBackground }] = useField('initial_media_file_remove_background')
    const [, , { setValue: setTemplateId }] = useField('source_visual_element_id')

    const { itemWidth, itemHeight, aspectRatio, items, twoByTwo } = useMobileTemplatePreviewDimensions({
      width,
      height,
    })
    const aspectRatioKey = getAspectRatioKeyFromDesignChannel(channel, width, height)

    const style: CSS.Properties = {
      ['--preview-width' as string]: `${itemWidth}px`,
      ['--preview-height' as string]: `${itemHeight}px`,
    }

    const filters = {
      element_type: 'Template' as 'Template',
      width,
      height,
      styles: 'all' as Selection,
      categories: 'all' as Selection,
    }

    const templatesQuery = useVisualTemplates(
      {
        ...filters,
        items,
        has_replaceable: true,
        replaceable_has_background: !removeBackground,
        order_by: 'random',
      },
      {
        cacheTime: 2 * 1000 * 60,
        staleTime: Infinity,
      },
    )

    const templates = collectResults(templatesQuery, (data) => data.visual_elements).slice(0, items)

    const handleSubmit = (templateId?: number) => {
      setTemplateId(templateId)
      submit()
    }

    const handleSelectTemplate = (templateId: number, newStack: boolean = false) => {
      drawersStore.openDrawer(
        TemplateDrawer,
        {
          initialTemplateId: templateId,
          onPressOutside: noop,
          initialAspectRatioKey: aspectRatioKey,
          isAspectRatioKeyEditable: false,
          onConfirm: () => {
            drawersStore.closeStack()
            handleSubmit(templateId)
          },
        },
        { newStack },
      )
    }

    const handleSeeAll = () => {
      drawersStore.openDrawer(
        TemplatesDrawerV2,
        {
          designChannel: wizardSchema.meta.channel as DesignChannel,
          disabledFilters: ['format'],
          onUse: () => {},
          onSelect: (templateId: number) => {
            handleSelectTemplate(templateId)
          },
          ...filters,
        },
        { newStack: true },
      )
    }

    return (
      <MobileWizard.Content>
        <MobileWizard.Section>
          <MobileWizard.Title>{meta.title}</MobileWizard.Title>
        </MobileWizard.Section>
        <div className={Styles.templatesContainer} style={style}>
          <TemplatesGrid
            onSelect={(item) => handleSubmit(item.id as number)}
            templates={templates}
            isLoading={templatesQuery.isLoading}
            aspectRatio={aspectRatio}
            twoByTwo={twoByTwo}
            templateBorderRadius={templateBorderRadius}
          />
          <Button variant="tertiary" size="lg" fullWidth onPress={handleSeeAll}>
            See All Templates
          </Button>
          <Button size="lg" variant="ghost" fullWidth onPress={() => handleSubmit()}>
            Skip
          </Button>
        </div>
      </MobileWizard.Content>
    )
  }),
)
